import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MAX_REASON_LENGTH = 500;

const ArchiveProjectModal = ({ isOpen, onClose, onConfirm, loading = false, error = "" }) => {
  const [reason, setReason] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const borderActive = isFocused || reason.trim() !== "";

  const handleConfirm = () => {
    if (loading) return;
    onConfirm(reason.trim());
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center font-outfit">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl py-6 px-6 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-6 right-6 text-gray-500 hover:text-gray-800 transition-colors disabled:opacity-50"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Title */}
        <h2 className="text-[22px] font-semibold text-[#3F3F46] mb-2">Archive Project</h2>

        <p className="text-[15px] text-[#52525B] leading-snug mb-5 pr-6">
          This project will be moved to the archive for everyone who has access.
          All collaborators on this project will be able to see that you archived it.
        </p>

        {/* Reason label */}
        <label className="block text-[15px] font-semibold text-[#3F3F46] mb-2">
          Reason <span className="font-normal text-[#A1A1AA]">(optional)</span>
        </label>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="e.g, No longer required!"
          disabled={loading}
          rows={4}
          className={`w-full rounded-lg border px-4 py-3 text-[15px] text-[#3F3F46] placeholder:text-[#A1A1AA] resize-none outline-none transition-colors duration-150 ${
            borderActive ? "border-[#C95EBE]" : "border-gray-300 hover:border-gray-400"
          }`}
        />
        <div className="flex justify-end mt-1">
          <span className="text-[12px] text-[#A1A1AA]">
            {reason.length}/{MAX_REASON_LENGTH}
          </span>
        </div>

        {error && (
          <p className="text-[13px] text-[#E21919] mt-1">{error}</p>
        )}

        {/* Buttons */}
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-10 py-2.5 rounded-lg border border-[#C95EBE] text-[#C95EBE] font-semibold text-[15px] bg-white hover:bg-[#C95EBE]/5 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-10 py-2.5 rounded-lg text-white font-semibold text-[15px] transition-colors"
            style={{
              backgroundColor: loading ? "#9CA3AF" : "#C95EBE",
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Archiving..." : "Archive Project"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ArchiveProjectModal;
